import React, { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";
import {
  Box,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Typography,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import "../App.css";

function TheCameraPage() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState(""); // Optional field
  const [jobRole, setJobRole] = useState(""); // Optional field
  const [isFaceDetected, setIsFaceDetected] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [storedRecord, setStoredRecord] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [multipleFacesWarning, setMultipleFacesWarning] = useState(false);
  const [recap, setRecap] = useState(false); // Flag for recapture

  const webcamRef = useRef(null);
  const navigate = useNavigate();

  // On mount, check for an existing valid record in localStorage.
  useEffect(() => {
    const record = localStorage.getItem("userPhotoData");
    if (record) {
      const parsed = JSON.parse(record);
      const now = new Date().getTime();
      if (now < parsed.expiry) {
        setStoredRecord(parsed);
        setName(parsed.name);
        setPhone(parsed.phone);
        setCompany(parsed.company || "");
        setJobRole(parsed.jobRole || "");
        setShowForm(false);
      } else {
        localStorage.removeItem("userPhotoData");
      }
    }
  }, []);

  // Simulated face detection (replace with your own logic if needed)
  const detectFace = () => {
    setTimeout(() => {
      setIsFaceDetected(true);
    }, 2000);
  };

  // Called when the form overlay is submitted.
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (name && phone) {
      setShowForm(false);
    } else {
      alert("Please fill in both your name and phone number.");
    }
  };

  // Capture the image from the webcam and send form data to the /save endpoint.
  const captureImage = async () => {
    if (!webcamRef.current) return;

    setCapturing(true);
    const imageSrc = webcamRef.current.getScreenshot();

    if (imageSrc) {
      const formData = new FormData();
      formData.append("file", dataURLtoFile(imageSrc, "image.jpg"));
      formData.append("name", name);
      formData.append("phone", phone);
      formData.append("company", company);
      formData.append("jobRole", jobRole);
      formData.append("recap", recap ? "true" : "false");

      try {
        const response = await fetch(
          "https://facerec.xdestudios.com/save",
          {
            method: "POST",
            body: formData,
          }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.error === "Multiple faces detected") {
            setMultipleFacesWarning(true);
          } else if (data.id) {
            // Set expiry for 24 hours
            const expiry = new Date().getTime() + 24 * 60 * 60 * 1000;
            const record = {
              name,
              phone,
              company,
              jobRole,
              expiry,
              data,
            };
            localStorage.setItem("userPhotoData", JSON.stringify(record));
            setStoredRecord(record);
            // Reset the recap flag after a successful capture.
            setRecap(false);
            navigate(`/user/${data.id}`);
          } else {
            alert("Error: " + data.error);
          }
        } else {
          alert("Failed to upload image. Please try again.");
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        alert("Error uploading image.");
      }
    }
    setCapturing(false);
  };

  // Utility: Convert a data URL to a File object.
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // When "Recapture Image" is clicked, clear stored record, show the form overlay, and set the recap flag.
  const handleRecapture = () => {
    localStorage.removeItem("userPhotoData");
    setStoredRecord(null);
    setShowForm(true);
    setRecap(true);
  };

  // Blur the background if the form overlay or warning is visible.
  const shouldBlur = showForm || storedRecord || multipleFacesWarning;

  return (
    <div className="camera-container">
      {/* Background Webcam */}
      <Webcam
        ref={webcamRef}
        className="webcam"
        audio={false}
        screenshotFormat="image/jpeg"
        videoConstraints={{ facingMode: "user" }}
        onUserMedia={detectFace}
        mirrored={true}
        screenshotQuality={0.7}
        style={{ filter: shouldBlur ? "blur(8px)" : "none" }}
      />

      {/* Optional White Oval (only if no form/record/warning) */}
      {!showForm && !storedRecord && !multipleFacesWarning && (
        <div
          className={`face-outline ${isFaceDetected ? "face-detected" : ""}`}
        ></div>
      )}

      {/* Info Card: Display if a stored record exists */}
      {storedRecord ? (
        <Paper
          elevation={3}
          className="info-card"
          sx={{
            position: "absolute",
            bottom: 20,
            width: "90%",
            maxWidth: 400,
            padding: 3,
            borderRadius: "16px",
            background: "rgba(40,29,68,0.9)",
            backdropFilter: "blur(10px)",
            zIndex: 3,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Typography variant="h6" sx={{ color: "white", mb: 2 }}>
            Your image has already been captured.
          </Typography>
          <Typography variant="body1" sx={{ color: "white", mb: 2 }}>
            Name: {storedRecord.name} | Phone: +91{storedRecord.phone}
          </Typography>
          {storedRecord.company && (
            <Typography variant="body2" sx={{ color: "white", mb: 1 }}>
              Company: {storedRecord.company}
            </Typography>
          )}
          {storedRecord.jobRole && (
            <Typography variant="body2" sx={{ color: "white", mb: 2 }}>
              Job Role: {storedRecord.jobRole}
            </Typography>
          )}
          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#ff6f61",
              "&:hover": { backgroundColor: "#e65c50" },
              mb: 1,
            }}
            onClick={() => navigate(`/user/${storedRecord.data.id}`)}
          >
            View Images
          </Button>
          <Button
            variant="outlined"
            fullWidth
            sx={{ borderColor: "#ff6f61", color: "#ff6f61" }}
            onClick={handleRecapture}
          >
            Recapture Image
          </Button>
        </Paper>
      ) : (
        <>
          {/* Form Overlay */}
          {showForm && (
            <Box
              component="form"
              onSubmit={handleFormSubmit}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgba(40,29,68,0.9)",
                padding: 3,
                borderRadius: 2,
                zIndex: 3,
                width: "90%",
                maxWidth: 400,
              }}
            >
              <Typography variant="h6" sx={{ color: "white", mb: 2 }}>
                Enter Your Details
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                label="Name"
                placeholder="Enter your name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                InputProps={{ style: { color: "white" } }}
                InputLabelProps={{ style: { color: "rgba(255,255,255,0.7)" } }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Phone Number"
                placeholder="Enter your phone number"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: "white" }}>+91</Typography>
                    </InputAdornment>
                  ),
                  style: { color: "white" },
                }}
                InputLabelProps={{ style: { color: "rgba(255,255,255,0.7)" } }}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Company Name (optional)"
                placeholder="Enter your company name"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                InputProps={{ style: { color: "white" } }}
                InputLabelProps={{ style: { color: "rgba(255,255,255,0.7)" } }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Job Role (optional)"
                placeholder="Enter your job role"
                value={jobRole}
                onChange={(e) => setJobRole(e.target.value)}
                InputProps={{ style: { color: "white" } }}
                InputLabelProps={{ style: { color: "rgba(255,255,255,0.7)" } }}
                sx={{ mb: 2 }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: "#ff6f61",
                  "&:hover": { backgroundColor: "#e65c50" },
                }}
              >
                Continue
              </Button>
            </Box>
          )}
          {/* Capture Button */}
          {!showForm && !storedRecord && (
            <Button
              onClick={captureImage}
              disabled={capturing}
              sx={{
                position: "absolute",
                bottom: 20,
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "#ff6f61",
                color: "white",
                borderRadius: "50%",
                width: 60,
                height: 60,
                zIndex: 3,
                "&:hover": { backgroundColor: "#e65c50" },
              }}
            >
              {capturing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <CameraAltIcon fontSize="large" />
              )}
            </Button>
          )}
        </>
      )}

      {/* Dialog for Multiple Faces Warning */}
      <Dialog
        open={multipleFacesWarning}
        onClose={() => setMultipleFacesWarning(false)}
        PaperProps={{
          style: { backgroundColor: "rgba(40,29,68,0.9)" },
        }}
      >
        <DialogTitle style={{ color: "#ff6f61" }}>
          Multiple Faces Detected
        </DialogTitle>
        <DialogContent>
          <Typography style={{ color: "#ff6f61" }}>
            Please ensure only your face is visible in the frame.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMultipleFacesWarning(false)}
            style={{ color: "#ff6f61" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default TheCameraPage;
